/**
 * Host-aware 404 surface. Same component renders on both hosts but the
 * copy and CTAs flip based on `getHostKind()`:
 *
 *  - admin → lists the 6 console destinations as quick recovery
 *  - product → simple "back to home" CTA
 */
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { Card, Heading, Stack } from '@francisquiniai/hestia-web'
import { isAdminHost } from '@/lib/host'

const CONSOLE_SUGGESTIONS = [
  { id: 'cockpit' as const, to: '/cockpit' },
  { id: 'peitho' as const, to: '/peitho' },
  { id: 'pheme' as const, to: '/pheme' },
  { id: 'kratos' as const, to: '/kratos' },
  { id: 'odysseus' as const, to: '/odysseus' },
  { id: 'thalia' as const, to: '/thalia' },
  { id: 'eunomia' as const, to: '/eunomia/review' },
  { id: 'admin' as const, to: '/admin' },
]

export function NotFound() {
  const { t } = useTranslation('console')
  const admin = isAdminHost()

  if (admin) {
    return (
      <main
        data-testid="console-not-found"
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <Card className="p-6 max-w-lg w-full">
          <Stack gap="4">
            <Heading level={2}>{t('notFound.title')}</Heading>
            <p className="text-sm text-muted">{t('notFound.body')}</p>

            <div>
              <p
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#64748b',
                  marginBottom: 8,
                }}
              >
                {t('notFound.suggestionsTitle')}
              </p>
              <ul
                data-testid="console-not-found-suggestions"
                style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}
              >
                {CONSOLE_SUGGESTIONS.map((s) => (
                  <li key={s.id}>
                    <Link to={s.to} style={{ color: '#0f766e', fontSize: 14 }}>
                      {t(`home.cards.${s.id}.title`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <Link
              to="/"
              data-testid="console-not-found-home"
              style={{
                display: 'inline-block',
                padding: '10px 18px',
                borderRadius: 999,
                background: '#0f766e',
                color: 'white',
                fontWeight: 600,
                textDecoration: 'none',
                alignSelf: 'flex-start',
              }}
            >
              {t('notFound.backHome')}
            </Link>
          </Stack>
        </Card>
      </main>
    )
  }

  return (
    <main
      data-testid="product-not-found"
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <Card className="p-6 max-w-md w-full text-center">
        <Stack gap="3">
          <Heading level={2}>{t('productNotFound.title')}</Heading>
          <p className="text-sm text-muted">{t('productNotFound.body')}</p>
          <Link
            to="/"
            data-testid="product-not-found-home"
            style={{
              display: 'inline-block',
              padding: '12px 20px',
              borderRadius: 999,
              background: '#0f766e',
              color: 'white',
              fontWeight: 600,
              textDecoration: 'none',
              alignSelf: 'center',
            }}
          >
            {t('productNotFound.backHome')}
          </Link>
        </Stack>
      </Card>
    </main>
  )
}
